import type {
  BusinessListItem,
  BusinessProfilesPayload,
  BusinessProfileSummary,
} from "../../services/businessProfileService";

interface DataContainer {
  data?: unknown;
}

interface BusinessListContainer extends DataContainer {
  businesses?: unknown;
  results?: unknown;
}

export const EMPTY_BUSINESS_PROFILES: BusinessProfilesPayload = {
  personal: {
    type: "personal",
    displayName: "",
    avatarUrl: null,
  },
  businesses: [],
  activeBusinessId: null,
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === "object";
};

const isBusinessProfilesPayload = (
  value: unknown
): value is BusinessProfilesPayload => {
  if (!isRecord(value)) {
    return false;
  }

  return Array.isArray(value.businesses);
};

export const normalizeBusinessProfilesPayload = (
  value: unknown
): BusinessProfilesPayload => {
  if (isBusinessProfilesPayload(value)) {
    return value;
  }

  if (isRecord(value)) {
    const nestedValue = (value as DataContainer).data;

    if (isBusinessProfilesPayload(nestedValue)) {
      return nestedValue;
    }
  }

  return EMPTY_BUSINESS_PROFILES;
};

export const normalizeBusinessList = (value: unknown): BusinessListItem[] => {
  if (Array.isArray(value)) {
    return value as BusinessListItem[];
  }

  if (!isRecord(value)) {
    return [];
  }

  const container = value as BusinessListContainer;

  if (Array.isArray(container.businesses)) {
    return container.businesses as BusinessListItem[];
  }

  if (Array.isArray(container.results)) {
    return container.results as BusinessListItem[];
  }

  if (Array.isArray(container.data)) {
    return container.data as BusinessListItem[];
  }

  return [];
};

const normalizeBusinessId = (value: string | null | undefined): string => {
  return (value || "").trim();
};

const normalizeBusinessName = (value: string | null | undefined): string => {
  return (value || "").trim().toLowerCase();
};

export const resolveBusinessLogoUrl = (
  business: BusinessProfileSummary | BusinessListItem | null | undefined
): string | null => {
  if (!business) {
    return null;
  }

  return (
    business.logoUrl ||
    business.avatarUrl ||
    business.logo ||
    business.imageUrl ||
    null
  );
};

const createBusinessIndex = (businessList: BusinessListItem[]) => {
  const byId = new Map<string, BusinessListItem>();
  const byName = new Map<string, BusinessListItem>();

  businessList.forEach((business) => {
    const businessId = normalizeBusinessId(business.id || business._id);
    const businessName = normalizeBusinessName(business.businessName);

    if (businessId && !byId.has(businessId)) {
      byId.set(businessId, business);
    }

    if (businessName && !byName.has(businessName)) {
      byName.set(businessName, business);
    }
  });

  return { byId, byName };
};

export const enrichBusinessProfiles = (
  profiles: BusinessProfilesPayload,
  businessList: BusinessListItem[]
): BusinessProfilesPayload => {
  const index = createBusinessIndex(businessList);

  return {
    ...profiles,
    businesses: profiles.businesses.map((business) => {
      const existingLogoUrl = resolveBusinessLogoUrl(business);

      if (existingLogoUrl) {
        return business;
      }

      const matchedBusiness =
        index.byId.get(normalizeBusinessId(business.businessId)) ||
        index.byName.get(normalizeBusinessName(business.businessName));

      const matchedLogoUrl = resolveBusinessLogoUrl(matchedBusiness);

      if (!matchedLogoUrl) {
        return business;
      }

      return {
        ...business,
        logoUrl: matchedLogoUrl,
      };
    }),
  };
};
